/**
 * Handles side panel
 * @module side-panel
 */

import $ from 'jquery';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import mergeOptions from 'styleguide/lib/merge-options';
import { get } from '../lib/props';
import Keyboard from './keyboard';
import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'sidePanel';

export const DEFAULTS = {
  url: '',
  onAjaxLoad: () => {},
  loadingText: get('rcProps.translations.sidePanel.loadingText', 'One moment please...'),
  loadingTextSR: get('rcProps.translations.sidePanel.loadingTextSR', 'Content is Loading'),
  returnFocus: '',
  trapFocusFunc: Keyboard.trapFocus.bind(Keyboard),
  releaseFocusFunc: Keyboard.releaseFocus.bind(Keyboard),
};

const openSidePanelFocus = ($el, opts) => {
  if ($el.hasClass('side-panel--open')) {
    // Look for anchors, buttons, and elements with tabindex not set to -1
    const $firstElementToFocus = $el.find('button, a, [tabindex]:not([tabindex="-1"])').first();
    $firstElementToFocus[0].focus();
    opts.trapFocusFunc($el);
  }
};

const closeSidePanelFocus = opts => {
  opts.releaseFocusFunc($('.side-panel'), $(opts.returnFocus));
};

const closePanel = ($panel, $container, opts) => {
  clearAllBodyScrollLocks($panel[0]);
  $container.removeClass('side-panel--open');
  if ($('#sortRefineBtn').length) {
    $('#sortRefineBtn').attr('aria-expanded', false);
    closeSidePanelFocus(opts);
  }
};

const getLoadingMessage = ({ loadingText, loadingTextSR }) => $(`
    <div class="nav__loading-screen u-center u-text--lg">
        <p class="u-margin-b--md">${loadingText}</p>
        <div class="progress-bar progress-bar--dark u-margin--center">
            <div class="u-screen-reader">
                ${loadingTextSR}
            </div>
        </div>
    </div>
`);

/**
 * Initializes side panel.
 * @param {HTMLElement} el - button /element that opens side panel
 * @param {Object} options - Options used for wiring up the side panel
 * @see DEFAULTS
 */
export default function sidePanel(el, options = {}) {
  const $button = $(el);
  const opts = mergeOptions(DEFAULTS, options, $button);

  const $container = $('<div class="side-panel" id="side-panel" aria-modal="true" aria-labelledby="sortRefineBtn" />');
  const $overlay = $('<div class="side-panel__overlay" id="side-panel__overlay" />');
  const $panel = $('<div class="side-panel__panel" />');
  const $loadingMessage = getLoadingMessage(opts);

  $panel.append($loadingMessage);

  $('body').append($container);
  $container.append($overlay);
  $container.append($panel);

  $overlay.on('click', () => closePanel($panel, $container, opts));

  // AJAX
  $.ajax({ method: 'GET', url: opts.url })
    .done((content) => {
      $panel.html(content);
      const $closeButtons = $panel.find('[side-panel-close]');
      $closeButtons.on('click', () => closePanel($panel, $container, opts));
      opts.onAjaxLoad();
    });
  $button.on('click', () => {
    disableBodyScroll($panel[0]);
    $container.addClass('side-panel--open');
    $button.attr('aria-expanded', true);
    setTimeout(() => {
      openSidePanelFocus($container, opts);
    }, 400);
  });
  // New arrivals page to open automatically if it's coming from side panel filter
  if (window.rcProps.isNewPage && window.location.href.indexOf('fromSidePanel=true') !== -1) {
    disableBodyScroll($panel[0]);
    $container.addClass('side-panel--open');
    $button.attr('aria-expanded', true);
    setTimeout(() => {
      openSidePanelFocus($container, opts);
    }, 400);
  }
}

registerJQueryPlugin(PLUGIN_NAME, sidePanel);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/side-panel.js